import React, { useState, useEffect } from 'react';
import { FaPlus } from 'react-icons/fa';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Radio,
  TextField,
  Chip,
  Autocomplete,
  Button
} from '@mui/material';
import { EmployeeAvatar } from '../../../../common/EmployeeAvatar';
import EmployeeSearch from '../../../../common/EmployeeSearch';
import { useAlert } from '../../../../../contexts/AlertContext';
import { feedbackApi } from '../../../../../services/api';
import { FEEDBACK_ON_DEMAND_TYPES } from '../../../../../constants/feedbackOnDemandTypes';
import { 
  FEEDBACK_ON_DEMAND_STATUSES,
  getFeedbackStatusColor,
  getFeedbackStatusTextColor,
  getFeedbackStatusText,
  determineFeedbackStatus,
  getFeedbackStatusBorderColor
} from '../../../../../constants/feedbackOnDemandStatuses';
import dayjs from 'dayjs';
import EmptyState from '../../../../common/EmptyState';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import Modal from '../../../../common/Modal';
import ConfirmationModal from '../../../../common/ConfirmationModal';
import {
  ActionButton,
  FeedbackCard,
  CardContent,
  FormContainer,
  ResultCount
} from './styles';

import { FormSection } from '../../styles';

import { COLORS } from '../../../../../constants/radar';
import { FeedbackOnDemandStatus, FeedbackOnDemandType } from '../../../../common/filters';

const STEPS = ['Select Type', 'Select Individual', 'Add Message'];

const TypeSelection = ({ selectedType, onTypeChange }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Choose the type of feedback interaction:
    </Typography>
    <Box sx={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(2, 1fr)', 
      gap: 2,
      mt: 1
    }}>
      <Box
        onClick={() => onTypeChange(FEEDBACK_ON_DEMAND_TYPES.ASKING)}
        sx={{
          cursor: 'pointer',
          p: 3,
          borderRadius: 2,
          border: '2px solid',
          borderColor: selectedType === FEEDBACK_ON_DEMAND_TYPES.ASKING ? '#2E90FA' : '#e2e8f0',
          backgroundColor: selectedType === FEEDBACK_ON_DEMAND_TYPES.ASKING ? '#f0f9ff' : '#fff',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#2E90FA',
            backgroundColor: '#f8fafc'
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          mb: 1
        }}>
          <Radio
            checked={selectedType === FEEDBACK_ON_DEMAND_TYPES.ASKING}
            onChange={() => onTypeChange(FEEDBACK_ON_DEMAND_TYPES.ASKING)}
            sx={{ 
              color: '#94a3b8',
              '&.Mui-checked': {
                color: '#2E90FA',
              }
            }}
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: selectedType === FEEDBACK_ON_DEMAND_TYPES.ASKING ? '#2E90FA' : '#0f172a'
            }}
          >
            Ask for Feedback
          </Typography>
        </Box>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ pl: 4 }}
        >
          Request feedback from a colleague about your performance or specific work
        </Typography>
      </Box>

      <Box
        onClick={() => onTypeChange(FEEDBACK_ON_DEMAND_TYPES.GIVING)}
        sx={{
          cursor: 'pointer',
          p: 3,
          borderRadius: 2,
          border: '2px solid',
          borderColor: selectedType === FEEDBACK_ON_DEMAND_TYPES.GIVING ? '#16A34A' : '#e2e8f0',
          backgroundColor: selectedType === FEEDBACK_ON_DEMAND_TYPES.GIVING ? '#f0fdf4' : '#fff',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#16A34A',
            backgroundColor: '#f8fafc'
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          mb: 1
        }}>
          <Radio
            checked={selectedType === FEEDBACK_ON_DEMAND_TYPES.GIVING}
            onChange={() => onTypeChange(FEEDBACK_ON_DEMAND_TYPES.GIVING)}
            sx={{ 
              color: '#94a3b8',
              '&.Mui-checked': {
                color: '#16A34A',
              }
            }}
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: selectedType === FEEDBACK_ON_DEMAND_TYPES.GIVING ? '#16A34A' : '#0f172a'
            }}
          >
            Give Feedback
          </Typography>
        </Box>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ pl: 4 }}
        >
          Provide constructive feedback to help a colleague improve and grow
        </Typography>
      </Box>
    </Box>
  </Box>
);

const EmployeeSelection = ({ selectedEmployee, onEmployeeChange, type }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {type === FEEDBACK_ON_DEMAND_TYPES.ASKING
        ? 'Select a individual to request feedback from:'
        : 'Select a individual to give feedback to:'}
    </Typography>

    {selectedEmployee && (
      <Box 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          mb: 2,
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}
      >
        <EmployeeAvatar employee={selectedEmployee} size={48} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>
            {selectedEmployee.fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedEmployee.email}
          </Typography>
        </Box>
      </Box>
    )}

    <EmployeeSearch
      value={selectedEmployee}
      onChange={onEmployeeChange}
      label={selectedEmployee ? "Change Individual" : "Search Individuals"}
      placeholder="Start typing to search..."
      width="100%"
      height="56px"
      sx={{ width: '100%' }}
      showSelectedAvatar={false}
    />
  </Box>
);

const MessageInput = ({ subject, onSubjectChange }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Add a subject for your feedback request:
    </Typography>
    <TextField
      label="Subject"
      fullWidth
      value={subject}
      onChange={(e) => onSubjectChange(e.target.value)}
      multiline
      rows={4}
      placeholder="Enter your message here..."
    />
  </Box>
);

const FeedbackCardComponent = ({ item, employeeId, onFeedbackAction }) => {
  const status = determineFeedbackStatus(item);
  const isReceiver = item.receiver.id === employeeId;
  const isSender = item.sender.id === employeeId;
  const isPending = status === FEEDBACK_ON_DEMAND_STATUSES.PENDING;
  const isCompleted = status === FEEDBACK_ON_DEMAND_STATUSES.COMPLETED;
  const canProvideFeedback = (
    // Case 1: Receiver can provide feedback when being asked for feedback
    (isReceiver && item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING) ||
    // Case 2: Sender can provide feedback when giving feedback
    (isSender && item.type === FEEDBACK_ON_DEMAND_TYPES.GIVING)
  ) && (
    // For both cases, status must be ACCEPTED or IN_PROGRESS
    status === FEEDBACK_ON_DEMAND_STATUSES.ACCEPTED || 
    status === FEEDBACK_ON_DEMAND_STATUSES.IN_PROGRESS
  );
  const { showAlert } = useAlert();
  
  const [showAcceptConfirmation, setShowAcceptConfirmation] = useState(false);
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false);
  const [showProvideFeedbackModal, setShowProvideFeedbackModal] = useState(false);
  const [showViewFeedbackModal, setShowViewFeedbackModal] = useState(false);
  const [startComments, setStartComments] = useState('');
  const [stopComments, setStopComments] = useState('');
  const [continueComments, setContinueComments] = useState('');
  
  const handleAccept = async () => {
    try {
      const updatedFeedback = await feedbackApi.acceptFeedback(item.id);
      Object.assign(item, updatedFeedback);
      showAlert('Feedback request accepted successfully', 'success');
      setShowAcceptConfirmation(false);
    } catch (error) {
      showAlert(error.message, 'error', error);
    }
  };

  const handleReject = async () => {
    try {
      const updatedFeedback = await feedbackApi.rejectFeedback(item.id);
      Object.assign(item, updatedFeedback);
      showAlert('Feedback request rejected successfully', 'success');
      setShowRejectConfirmation(false);
    } catch (error) {
      showAlert(error.message, 'error', error);
    }
  };

  const handleProvideFeedbackClick = async () => {
    try {
      if (status === FEEDBACK_ON_DEMAND_STATUSES.ACCEPTED) {
        const updatedFeedback = await feedbackApi.startFeedback(item.id);
        Object.assign(item, updatedFeedback);
      }
      setShowProvideFeedbackModal(true);
    } catch (error) {
      showAlert(error.message || 'Failed to start feedback', 'error', error);
    }
  };

  const handleProvideFeedback = async (feedbackData) => {
    try {
      const payload = {
      };

      // Only add comments that have content
      if (feedbackData.startComments?.trim()) {
        payload.startComments = feedbackData.startComments.trim();
      }
      if (feedbackData.stopComments?.trim()) {
        payload.stopComments = feedbackData.stopComments.trim();
      }
      if (feedbackData.continueComments?.trim()) {
        payload.continueComments = feedbackData.continueComments.trim();
      }

      const updatedFeedback = await feedbackApi.provideFeedback(item.id, payload);
      Object.assign(item, updatedFeedback);
      showAlert('Feedback provided successfully', 'success');
      setShowProvideFeedbackModal(false);
      
      // Reset form values
      setStartComments('');
      setStopComments('');
      setContinueComments('');
    } catch (error) {
      showAlert(error.message, 'error', error);
    }
  };
  
  return (
    <>
      <FeedbackCard>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            mb: 3 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmployeeAvatar employee={item.sender} size={40} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#0f172a' }}>
                    {item.sender.fullName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {dayjs(item.createdAt).format('MMM D, YYYY')}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING ? '#2E90FA' : '#16A34A',
                      fontWeight: 500,
                      display: 'block'
                    }}
                  >
                    {item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING ? 'Asking for Feedback' : 'Giving Feedback'}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                →
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmployeeAvatar employee={item.receiver} size={40} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#0f172a' }}>
                    {item.receiver.fullName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receiver
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Chip
              label={getFeedbackStatusText(status)}
              sx={{
                backgroundColor: getFeedbackStatusColor(status),
                color: getFeedbackStatusTextColor(status),
                fontWeight: 500,
                fontSize: '12px',
                border: '1px solid',
                borderColor: getFeedbackStatusBorderColor(status)
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: COLORS.PRIMARY, mb: 1 }}>
                {item.subject}
              </Typography>
            </Box>

            {isReceiver && isPending && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 2,
                mt: 'auto',
                pt: 3,
                borderTop: '1px solid #e2e8f0'
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowAcceptConfirmation(true)}
                  sx={{
                    borderColor: '#16A34A',
                    color: '#16A34A',
                    minWidth: '100px',
                    '&:hover': {
                      borderColor: '#15803D',
                      backgroundColor: '#F0FDF4'
                    }
                  }}
                >
                  Accept
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowRejectConfirmation(true)}
                  sx={{
                    borderColor: '#DC2626',
                    color: '#DC2626',
                    minWidth: '100px',
                    '&:hover': {
                      borderColor: '#B91C1C',
                      backgroundColor: '#FEF2F2'
                    }
                  }}
                >
                  Reject
                </Button>
              </Box>
            )}

            {canProvideFeedback && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 2,
                mt: 'auto',
                pt: 3,
                borderTop: '1px solid #e2e8f0'
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleProvideFeedbackClick}
                  sx={{
                    borderColor: '#2E90FA',
                    color: '#2E90FA',
                    minWidth: '150px',
                    '&:hover': {
                      borderColor: '#1D4ED8',
                      backgroundColor: '#EFF6FF'
                    }
                  }}
                >
                  Provide Feedback
                </Button>
              </Box>
            )}

            {isCompleted && (isSender || isReceiver) && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 2,
                mt: 'auto',
                pt: 3,
                borderTop: '1px solid #e2e8f0'
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowViewFeedbackModal(true)}
                  sx={{
                    borderColor: '#2E90FA',
                    color: '#2E90FA',
                    minWidth: '150px',
                    '&:hover': {
                      borderColor: '#1D4ED8',
                      backgroundColor: '#EFF6FF'
                    }
                  }}
                >
                  View Feedback
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </FeedbackCard>

      <ConfirmationModal
        isOpen={showAcceptConfirmation}
        onClose={() => setShowAcceptConfirmation(false)}
        onConfirm={handleAccept}
        title="Accept Feedback Request"
        message={`Are you sure you want to accept the feedback request from ${item.sender.fullName}?`}
        actionButtonText="Accept"
        actionButtonColor="#16A34A"
      />

      <ConfirmationModal
        isOpen={showRejectConfirmation}
        onClose={() => setShowRejectConfirmation(false)}
        onConfirm={handleReject}
        title="Reject Feedback Request"
        message={`Are you sure you want to reject the feedback request from ${item.sender.fullName}?`}
        actionButtonText="Reject"
        actionButtonColor="#DC2626"
      />

      <Modal
        isOpen={showProvideFeedbackModal}
        onClose={() => {
          setShowProvideFeedbackModal(false);
          setStartComments('');
          setStopComments('');
          setContinueComments('');
        }}
        title={`Providing feedback to ${item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING ? item.sender.fullName : item.receiver.fullName}`}
        submitText="Submit Feedback"
        cancelText="Cancel"
        onSubmit={() => {
          handleProvideFeedback({
            startComments,
            stopComments,
            continueComments
          });
        }}
        width="600px"
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            backgroundColor: '#f8fafc',
            p: 2,
            borderRadius: 1,
            border: '1px solid #e2e8f0',
            mb: 3
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Original Request Subject
            </Typography>
            <Typography variant="body1" color="text.primary">
              {item.subject}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <TextField
                id="startComments"
                name="startComments"
                label="Start"
                value={startComments}
                onChange={(e) => setStartComments(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={`What should ${item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING ? item.sender.fullName : item.receiver.fullName} start doing?`}
                helperText={`What should ${item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING ? item.sender.fullName : item.receiver.fullName} start doing?`}
              />
            </Box>

            <Box>
              <TextField
                id="stopComments"
                name="stopComments"
                label="Stop"
                value={stopComments}
                onChange={(e) => setStopComments(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={`What should ${item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING ? item.sender.fullName : item.receiver.fullName} stop doing?`}
                helperText={`What should ${item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING ? item.sender.fullName : item.receiver.fullName} stop doing?`}
              />
            </Box>

            <Box>
              <TextField
                id="continueComments"
                name="continueComments"
                label="Continue"
                value={continueComments}
                onChange={(e) => setContinueComments(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={`What should ${item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING ? item.sender.fullName : item.receiver.fullName} continue doing?`}
                helperText={`What should ${item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING ? item.sender.fullName : item.receiver.fullName} continue doing?`}
              />
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        isOpen={showViewFeedbackModal}
        onClose={() => setShowViewFeedbackModal(false)}
        title={`Feedback ${item.type === FEEDBACK_ON_DEMAND_TYPES.ASKING ? 'from' : 'to'} ${item.receiver.fullName}`}
        submitText="Close"
        showCancelButton={false}
        onSubmit={() => setShowViewFeedbackModal(false)}
        width="600px"
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            backgroundColor: '#f8fafc',
            p: 2,
            borderRadius: 1,
            border: '1px solid #e2e8f0',
            mb: 3
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Original Request Subject
            </Typography>
            <Typography variant="body1" color="text.primary">
              {item.subject}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {item.startComments && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', mb: 1 }}>
                  Start
                </Typography>
                <Box sx={{ 
                  backgroundColor: '#f8fafc',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid #e2e8f0'
                }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }}
                  >
                    {item.startComments}
                  </Typography>
                </Box>
              </Box>
            )}

            {item.stopComments && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', mb: 1 }}>
                  Stop
                </Typography>
                <Box sx={{ 
                  backgroundColor: '#f8fafc',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid #e2e8f0'
                }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }}
                  >
                    {item.stopComments}
                  </Typography>
                </Box>
              </Box>
            )}

            {item.continueComments && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', mb: 1 }}>
                  Continue
                </Typography>
                <Box sx={{ 
                  backgroundColor: '#f8fafc',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid #e2e8f0'
                }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }}
                  >
                    {item.continueComments}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

const FeedbackModal = ({ isOpen, onClose, onSubmit, employeeId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedType, setSelectedType] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [subject, setSubject] = useState('');

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handleSubmit = () => {
    onSubmit({
      type: selectedType,
      senderId: employeeId,
      receiverId: selectedEmployee.id,
      subject
    });
    handleClose();
  };

  const handleClose = () => {
    setCurrentStep(0);
    setSelectedType('');
    setSelectedEmployee(null);
    setSubject('');
    onClose();
  };

  const isNextDisabled = () => {
    switch (currentStep) {
      case 0:
        return !selectedType;
      case 1:
        return !selectedEmployee;
      case 2:
        return !subject;
      default:
        return false;
    }
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <TypeSelection
            selectedType={selectedType}
            onTypeChange={setSelectedType}
          />
        );
      case 1:
        return (
          <EmployeeSelection
            selectedEmployee={selectedEmployee}
            onEmployeeChange={setSelectedEmployee}
            type={selectedType}
          />
        );
      case 2:
        return (
          <MessageInput
            subject={subject}
            onSubjectChange={setSubject}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Instant Feedback"
      submitText={currentStep === STEPS.length - 1 ? 'Submit' : 'Next'}
      cancelText={currentStep === 0 ? 'Cancel' : 'Back'}
      onSubmit={currentStep === STEPS.length - 1 ? handleSubmit : handleNext}
      onCancel={currentStep === 0 ? handleClose : handleBack}
      submitDisabled={isNextDisabled()}
      width="600px"
    >
      <Box sx={{ width: '100%', mb: 4 }}>
        <Stepper activeStep={currentStep}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      {getStepContent()}
    </Modal>
  );
};

const InstantFeedback = ({ employeeId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([
    /*FEEDBACK_ON_DEMAND_STATUSES.PENDING,
    FEEDBACK_ON_DEMAND_STATUSES.ACCEPTED,
    FEEDBACK_ON_DEMAND_STATUSES.IN_PROGRESS,
    FEEDBACK_ON_DEMAND_STATUSES.COMPLETED,
    FEEDBACK_ON_DEMAND_STATUSES.REJECTED*/
  ]);
  const { showAlert } = useAlert();

  useEffect(() => {
    fetchFeedback();
  }, [employeeId]);

  const fetchFeedback = async () => {
    try {
      setIsLoading(true);
      const response = await feedbackApi.getFeedback({ employeeId });
      setFeedback(response);
    } catch (error) {
      showAlert(error.message, 'error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitFeedback = async (formData) => {
    try {
      if (formData.type === FEEDBACK_ON_DEMAND_TYPES.ASKING) {
        await feedbackApi.askForFeedback(formData);
        showAlert('Feedback request sent successfully', 'success');
      } else {
        await feedbackApi.giveFeedback(formData);
        showAlert('Feedback request sent successfully', 'success');
      }
      fetchFeedback();
    } catch (error) {
      showAlert(error.message, 'error', error);
    }
  };

  const handleTypeToggle = (type) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleStatusToggle = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const filteredFeedback = feedback.filter(item => {
    const typeMatch = selectedTypes.length === 0 || selectedTypes.includes(item.type);
    const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(determineFeedbackStatus(item));
    return typeMatch && statusMatch;
  });

  const renderFeedbackList = () => {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (filteredFeedback.length === 0) {
      return (
        <EmptyState
          title="No feedback found"
          description="There is no feedback history yet. Try adjusting your filters."
        />
      );
    }

    return (
      <>
        <ResultCount>
          <span>{filteredFeedback.length}</span> {filteredFeedback.length === 1 ? 'feedback' : 'feedbacks'} found
        </ResultCount>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
          gap: 3
        }}>
          {filteredFeedback.map((item) => (
            <FeedbackCardComponent
              key={item.id}
              item={item}
              employeeId={employeeId}
              onFeedbackAction={fetchFeedback}
            />
          ))}
        </Box>
      </>
    );
  };

  return (
    <FormContainer>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 2,
        marginBottom: '24px'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FeedbackOnDemandType 
              selectedTypes={selectedTypes}
              onTypeToggle={handleTypeToggle}
            />
            
            <Box 
              sx={{ 
                height: '24px', 
                width: '1px', 
                backgroundColor: '#E2E8F0', 
                margin: '0 8px' 
              }} 
            />
            
            <FeedbackOnDemandStatus 
              selectedStatuses={selectedStatuses}
              onStatusToggle={handleStatusToggle}
            />
          </Box>
          <ActionButton onClick={() => setModalOpen(true)}>
            <FaPlus /> Instant Feedback
          </ActionButton>
        </Box>

      </Box>

      <FormSection>
        {renderFeedbackList()}
      </FormSection>

      <FeedbackModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmitFeedback}
        employeeId={employeeId}
      />
    </FormContainer>
  );
};

export default InstantFeedback; 